import * as React from 'react'
import { FunctionComponent, memo, useMemo } from 'react'
import { Formik } from 'formik'
import { indexBy, map, reject, pick, sortBy } from 'lodash/fp'

import Modal from '../../components/Modal'
import Input from '../../components/Input'
import Image from '../../components/Image'
import Select from '../../components/Select'
import Button from '../../components/Button'
import { IBranch, IDivision, IUser, IAccount } from '../../types/user'
import useThunkDispatch from '../../common/useThunkDispatch'
import {
  removeAccountTechnician,
  updateAccountUser,
  assignMultipleAccounts,
  requestAccounts,
} from '../accountsActions'
import { nameableToOption } from '../../common/name'
import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { accountsSelector } from '../accountsSelectors'
import extractErrors from '../../common/extractErrors'
import { compact } from 'lodash'
import { components as selectComponents } from 'react-select'

interface IOwnProps {
  user: Partial<IUser>
  branches: IBranch[]
  divisions: IDivision[]
  accountId: number
  onClose(): void
  onSave(): void
}

const AccountUserEdit: FunctionComponent<IOwnProps> = ({
  user,
  branches,
  divisions,
  accountId,
  onClose,
  onSave,
}) => {
  const dispatch = useThunkDispatch()
  const accounts = useSelector(accountsSelector) || []

  const isTechnician = user.role === 'technician'
  const [selectedAccounts, setSelectedAccounts] = useState<IAccount[]>([])
  const [accountBranches, setAccountBranches] = useState<{
    [accountId: number]: number[]
  }>({})
  const [hasAccountChanges, setHasAccountChanges] = useState(false)

  useEffect(() => {
    dispatch(requestAccounts({}))
  }, [dispatch])

  useEffect(() => {
    setHasAccountChanges(selectedAccounts.length > 0)
  }, [selectedAccounts, accountBranches])

  const initializeUserAccounts = () => {
    if (!user.id || !accountId) return

    const currentAccount = accounts.find((acc) => acc.id === accountId)
    if (!currentAccount || selectedAccounts.length > 0) return

    const allAccounts = [currentAccount]
    const initialAccountBranches: { [accountId: number]: number[] } = {
      [accountId]: user.branches?.map((b) => b.id) || [],
    }

    if (
      !isTechnician &&
      user.clonedUsers?.length &&
      user.availableAccounts?.length
    ) {
      const addedAccountIds = new Set([currentAccount.id])

      const clonedUserBranchIds = new Set<number>()
      user.clonedUsers.forEach((clonedUser) => {
        clonedUser.branches?.forEach((branch) => {
          clonedUserBranchIds.add(branch.id)
        })
      })

      user.availableAccounts.forEach((account) => {
        if (addedAccountIds.has(account.id)) return

        allAccounts.push(account)
        addedAccountIds.add(account.id)

        const accountBranchIds = new Set(
          account.branches?.map((b) => b.id) || [],
        )
        const matchingBranchIds = [...clonedUserBranchIds].filter((id) =>
          accountBranchIds.has(id),
        )

        initialAccountBranches[account.id] = matchingBranchIds
      })
    }

    setSelectedAccounts(allAccounts)
    setAccountBranches(initialAccountBranches)
  }

  useEffect(() => {
    initializeUserAccounts()
  }, [
    user.id,
    accountId,
    accounts,
    user.branches,
    user.clonedUsers,
    selectedAccounts.length,
  ])

  const accountOptions = useMemo(
    () => sortBy('name', compact(map(nameableToOption, accounts))),
    [accounts],
  )
  const accountDict = useMemo(() => indexBy('id', accounts), [accounts])

  const accountSelectComponents = useMemo(
    () => ({
      MultiValueRemove: (
        props: React.ComponentProps<typeof selectComponents.MultiValueRemove>,
      ) => {
        if (props.data.value === accountId) return null

        return <selectComponents.MultiValueRemove {...props} />
      },
    }),
    [accountId],
  )

  const handleAccountsChange = useCallback(
    (selected: Array<{ value: number; label: string }>) => {
      let newSelectedAccounts = []
        .concat((selected as any) || [])
        .map((value: { value: number }) => accountDict[value.value])
        .filter(Boolean) as IAccount[]

      const primaryAccount = accountDict[accountId]
      if (
        primaryAccount &&
        !newSelectedAccounts.some((account) => account.id === accountId)
      ) {
        newSelectedAccounts = [primaryAccount, ...newSelectedAccounts]
      }

      setSelectedAccounts(newSelectedAccounts)
    },
    [accountDict, accountId],
  )

  const branchOptions = useMemo(
    () => sortBy('name', compact(map(nameableToOption, branches))),
    [branches],
  )
  const branchDict = useMemo(() => indexBy('id', branches), [branches])

  const divisionsOptions = useMemo(
    () => compact(map(nameableToOption, divisions)),
    [divisions],
  )
  const divisionsDict = useMemo(() => indexBy('id', divisions), [divisions])

  const filteredBranchOptions = (attrs: Partial<IUser>) => {
    const rejectMap = indexBy('id', attrs.branches)
    return reject((op) => !!rejectMap[op.value], branchOptions)
  }

  const filteredDivisionOptions = (attrs: Partial<IUser>) => {
    const branchId = attrs.branches?.[0]?.id
    const branchDivisions = branchId
      ? divisions.filter((d) => d.branchId === branchId)
      : divisions
    const rejectMap = indexBy('id', attrs.divisions)
    return reject(
      (op) => !!rejectMap[op.value],
      compact(map(nameableToOption, branchDivisions)),
    )
  }

  const onSubmit = async (attrs: Partial<IUser>, actions: any) => {
    actions.setSubmitting(true)
    try {
      if (!isTechnician) {
        const accountAssignments = selectedAccounts.map((account) => ({
          accountId: account.id,
          branchIds: accountBranches[account.id] || [],
        }))

        const errors = await dispatch(
          assignMultipleAccounts(attrs.id!, accountAssignments),
        )

        if (!errors) {
          await onSave()
          onClose()
        } else {
          alert(extractErrors(errors))
          actions.setErrors(errors)
        }
      } else {
        const userAttributes: Partial<IUser> & { avatar?: File } = pick(
          [
            'id',
            'role',
            'name',
            'email',
            'branches',
            'divisions',
            'checkoutManager',
            'binCount',
          ],
          attrs,
        )
        if (user.avatar !== attrs.avatar)
          userAttributes.avatar = attrs.avatar as any

        const errors = await dispatch(
          updateAccountUser({ ...userAttributes, accountId }),
        )
        if (!errors) {
          await onSave()
          onClose()
        } else actions.setErrors(errors)
      }
    } catch (e) {
      alert('Server Error')
    }
    actions.setSubmitting(false)
  }

  const [deleting, setDeleting] = useState(false)
  const onDelete = async () => {
    if (!user.id) return
    if (user.role !== 'technician') return
    if (!confirm('Are you sure you want to delete the user?')) return

    setDeleting(true)
    try {
      const errors = await dispatch(removeAccountTechnician(user.id))

      if (errors) alert(extractErrors(errors))
      else {
        await onSave()
        onClose()
      }
    } catch (e) {
      alert('Server Error')
    }
    setDeleting(false)
  }

  return (
    <Modal onClose={onClose} title="Edit User">
      <Formik
        initialValues={user}
        onSubmit={onSubmit}
        render={({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          dirty,
        }) => (
          <form className="form" onSubmit={handleSubmit}>
            <div className="form_group"></div>
            <div className="upload-avatar">
              <label className="upload-avatar_circle">
                <input
                  type="file"
                  name="avatar"
                  className="upload-avatar_input"
                  onChange={(e) => setFieldValue('avatar', e.target.files![0])}
                />
                {values.avatar && (
                  <Image
                    className="upload-avatar_image"
                    src={values.avatar}
                    alt=""
                  />
                )}
              </label>
            </div>
            <div className="form_group">
              <Input
                disabled={!isTechnician}
                name="name"
                label="Name"
                error={errors.name}
                value={values.name || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form_group">
              <Input
                disabled={!isTechnician}
                error={errors.email || ''}
                name="email"
                label="Email"
                type="email"
                value={values.email}
                onChange={handleChange}
              />
            </div>
            {!isTechnician && (
              <div className="form_group">
                <Select<number>
                  label="Accounts"
                  isMulti
                  options={accountOptions}
                  value={compact(map(nameableToOption, selectedAccounts))}
                  components={accountSelectComponents}
                  onChange={handleAccountsChange}
                />
              </div>
            )}
            {isTechnician && (
              <div className="form_group">
                <Select<number>
                  label="Branch"
                  isMulti={false}
                  options={filteredBranchOptions(values)}
                  value={compact(map(nameableToOption, values.branches || []))}
                  error={errors.branches}
                  onChange={(
                    selected: Array<{ value: number; label: string }>,
                  ) => {
                    setFieldValue(
                      'branches',
                      []
                        .concat((selected as any) || [])
                        .map(
                          (value: { value: number }) => branchDict[value.value],
                        ),
                    )
                    setFieldValue('divisions', [])
                  }}
                />
              </div>
            )}
            {!isTechnician &&
              selectedAccounts.map((account) => (
                <div key={account.id} className="form_group">
                  <Select<number>
                    label={`${account.name} Branches`}
                    isMulti
                    options={compact(
                      map(nameableToOption, account.branches || []),
                    )}
                    value={compact(
                      map(
                        nameableToOption,
                        (accountBranches[account.id] || [])
                          .map((branchId) =>
                            account.branches?.find((b) => b.id === branchId),
                          )
                          .filter(Boolean),
                      ),
                    )}
                    onChange={(
                      selected: Array<{ value: number; label: string }>,
                    ) => {
                      const branchIds = []
                        .concat((selected as any) || [])
                        .map((value: { value: number }) => value.value)
                      setAccountBranches((prev) => ({
                        ...prev,
                        [account.id]: branchIds,
                      }))
                    }}
                  />
                </div>
              ))}
            {user.role === 'technician' && (
              <div className="form_group">
                <Select<number>
                  label="Divisions"
                  isMulti
                  options={filteredDivisionOptions(values)}
                  value={compact(map(nameableToOption, values.divisions || []))}
                  error={errors.divisions}
                  onChange={(
                    selected: Array<{ value: number; label: string }>,
                  ) =>
                    setFieldValue(
                      'divisions',
                      []
                        .concat((selected as any) || [])
                        .map(
                          (value: { value: number }) =>
                            divisionsDict[value.value],
                        ),
                    )
                  }
                />
              </div>
            )}
            <div className="form_controls">
              <div className="form_controls-container">
                {user.id && user.role === 'technician' && (
                  <Button
                    disabled={deleting}
                    onClick={onDelete}
                    color="red-link"
                    className="form_control -left"
                  >
                    Delete user
                  </Button>
                )}
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  color="default-link"
                  className="form_control -size -right"
                >
                  Cancel
                </Button>
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitting || (!dirty && !hasAccountChanges)}
                  type="submit"
                  color="success"
                  className="form_control -size"
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      />
    </Modal>
  )
}

export default memo(AccountUserEdit)
